import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { sharingApi, type ComputerSharingInfo, type ShareCapability } from '@/lib/api/sharing';
import type { Agent } from '@/types/agent';

const labels: Record<string, string> = {
  'agent.view': 'Ver',
  'agent.control': 'Control remoto',
  'agent.chat': 'Chat',
  'agent.terminal': 'Terminal',
  'agent.manage': 'Administrar',
};
function describeError(error: unknown): string {
  const code = error instanceof Error ? error.message : '';
  if (code.includes('username_taken'))
    return 'Ese nombre de usuario ya existe. Podés compartir con esa cuenta.';
  if (code.includes('weak_password')) return 'La contraseña no cumple los requisitos.';
  if (code.includes('registration_disabled'))
    return 'Sólo un administrador de la plataforma puede crear usuarios.';
  if (code.includes('recipient_not_found'))
    return 'No existe un usuario registrado con ese nombre.';
  if (code.includes('recipient_has_organization_access'))
    return 'Ese usuario ya tiene acceso por su rol en la organización. Compartir no limita los permisos de ese rol.';
  if (code.includes('access_denied'))
    return 'No tenés permiso para gestionar el acceso a esta computadora.';
  return 'No se pudo guardar o consultar el acceso. Volvé a intentar.';
}
export function ComputerSharingDialog({
  agents,
  initialAgentId,
  onClose,
  onClaim,
  canCreateUsers = false,
}: {
  agents: Agent[];
  initialAgentId?: string;
  onClose: () => void;
  onClaim: (agentId: string) => void;
  canCreateUsers?: boolean;
}) {
  const [agentId, setAgentId] = useState(initialAgentId || agents[0]?.agentId || '');
  const [info, setInfo] = useState<ComputerSharingInfo | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [username, setUsername] = useState('');
  const [caps, setCaps] = useState<ShareCapability[]>(['agent.view']);
  const [duration, setDuration] = useState('24');
  const [notice, setNotice] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  const [initialPassword, setInitialPassword] = useState('');
  useEffect(() => {
    let active = true;
    setInfo(null);
    setError('');
    setNotice('');
    if (agentId) {
      void sharingApi
        .info(agentId)
        .then((value) => {
          if (active) setInfo(value);
        })
        .catch((reason: unknown) => {
          if (active) setError(describeError(reason));
        });
    }
    return () => {
      active = false;
    };
  }, [agentId]);
  const mutate = async (operation: () => Promise<void>, message: string) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await operation();
      setNotice(message);
      setInfo(await sharingApi.info(agentId));
    } catch (reason) {
      setError(describeError(reason));
    } finally {
      setBusy(false);
    }
  };
  const share = () =>
    mutate(async () => {
      await sharingApi.share({
        agentId,
        username: username.trim(),
        capabilities: caps,
        expiresAt:
          duration === '0'
            ? null
            : new Date(Date.now() + Number(duration) * 3600_000).toISOString(),
      });
      setUsername('');
    }, 'Acceso guardado. El usuario verá esta computadora en su lista.');
  return (
    <Dialog
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Compartir computadora</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label="Computadora"
            value={agentId}
            disabled={busy || agents.length === 0}
            onChange={(event) => {
              setInfo(null);
              setAgentId(event.target.value);
            }}
          >
            {agents.map((agent) => (
              <MenuItem key={agent.agentId} value={agent.agentId}>
                {agent.hostname || agent.agentId}
              </MenuItem>
            ))}
          </TextField>
          {agents.length === 0 && <Alert severity="info">No hay computadoras disponibles.</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {notice && <Alert severity="success">{notice}</Alert>}
          {agentId && !info && !error && <Typography>Cargando permisos…</Typography>}
          {info && !info.organizationId && (
            <Alert severity="info">
              Asigná primero esta computadora a una organización para poder compartirla.
              <Box mt={1}>
                <Button onClick={() => onClaim(agentId)}>Asignar a organización</Button>
              </Box>
            </Alert>
          )}
          {info?.organizationId && <Typography>Organización: {info.organizationName}</Typography>}
          {info?.organizationId && !info.canShare && (
            <Alert severity="info">
              Sólo el propietario o un gestor de esta organización puede compartir computadoras. Tus
              permisos: {info.capabilities.map((cap) => labels[cap] || cap).join(', ')}.
            </Alert>
          )}
          {info?.canShare && (
            <>
              <Typography variant="body2">
                El destinatario recibirá acceso únicamente a esta computadora. Ingresá su nombre de
                usuario registrado. Compartir nuevamente reemplaza sus permisos para este equipo.
              </Typography>
              <TextField
                label="Nombre de usuario"
                value={username}
                disabled={busy}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="off"
              />
              {canCreateUsers && (
                <>
                  <Button
                    disabled={busy}
                    onClick={() => {
                      setCreatingUser((value) => !value);
                      setInitialPassword('');
                    }}
                  >
                    {creatingUser ? 'Usar usuario existente' : 'Crear usuario nuevo'}
                  </Button>
                  {creatingUser && (
                    <>
                      <TextField
                        label="Contraseña inicial"
                        type="password"
                        autoComplete="new-password"
                        value={initialPassword}
                        disabled={busy}
                        inputProps={{ maxLength: 72 }}
                        helperText="Al menos 12 caracteres. Entregala al usuario para que pueda iniciar sesión."
                        onChange={(event) => setInitialPassword(event.target.value)}
                      />
                      <Button
                        disabled={busy || username.trim().length < 3 || initialPassword.length < 12}
                        onClick={() => {
                          void mutate(async () => {
                            await sharingApi.createUser(username.trim(), initialPassword);
                            setInitialPassword('');
                            setCreatingUser(false);
                          }, 'Usuario creado. Ahora podés guardar su acceso a esta computadora.');
                        }}
                      >
                        Crear cuenta
                      </Button>
                    </>
                  )}
                </>
              )}
              <Box>
                <Typography variant="subtitle2">Permisos</Typography>
                <FormControlLabel control={<Checkbox checked disabled />} label="Ver computadora" />
                {(['agent.control', 'agent.chat', 'agent.terminal'] as ShareCapability[]).map(
                  (cap) => (
                    <FormControlLabel
                      key={cap}
                      label={labels[cap]}
                      control={
                        <Checkbox
                          disabled={busy}
                          checked={caps.includes(cap)}
                          onChange={(_, checked) =>
                            setCaps((current) =>
                              checked ? [...current, cap] : current.filter((value) => value !== cap)
                            )
                          }
                        />
                      }
                    />
                  )
                )}
              </Box>
              {!caps.includes('agent.control') && (
                <Alert severity="info">
                  Sin control remoto no podrá usar teclado ni mouse. En agentes anteriores, la
                  visualización también permite leer el portapapeles; actualizá el agente para
                  restringirlo.
                </Alert>
              )}
              <TextField
                select
                label="Duración"
                value={duration}
                disabled={busy}
                onChange={(event) => setDuration(event.target.value)}
              >
                <MenuItem value="1">Una hora</MenuItem>
                <MenuItem value="24">Un día</MenuItem>
                <MenuItem value="168">Una semana</MenuItem>
                <MenuItem value="0">Hasta revocar</MenuItem>
              </TextField>
              <Button
                variant="contained"
                disabled={busy || creatingUser || !username.trim()}
                onClick={() => {
                  void share();
                }}
              >
                Guardar acceso
              </Button>
              <Typography variant="subtitle1">Accesos compartidos</Typography>
              {info.shares.length === 0 && (
                <Typography variant="body2">Todavía no compartiste esta computadora.</Typography>
              )}
              {info.shares.map((item) => (
                <Box
                  key={item.id}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{item.username}</Typography>
                    <Button
                      color="error"
                      disabled={busy}
                      onClick={() => {
                        void mutate(() => sharingApi.revoke(item.id), 'Acceso revocado.');
                      }}
                    >
                      Revocar
                    </Button>
                  </Stack>
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    {item.capabilities.map((cap) => (
                      <Chip key={cap} size="small" label={labels[cap] || cap} />
                    ))}
                  </Stack>
                  <Typography variant="caption">
                    {item.expiresAt
                      ? `${new Date(item.expiresAt).getTime() <= Date.now() ? 'Venció' : 'Vence'}: ${new Date(item.expiresAt).toLocaleString()}`
                      : 'Hasta revocar'}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
